import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { clamp, pixelToTime } from '../utils/time-calculations';

export type TimelineDragHandle = 'start' | 'end';
const EDGE_GUTTER_PX = 48;
const EDGE_ACTIVATION_MS = 80;
const EDGE_SPEED_MIN_MS_PER_SEC = 150;
const EDGE_SPEED_MAX_MS_PER_SEC = 1200;

type TimelineDragCallbacks = {
  onTrimStart: (segmentId: string, newStartMs: number) => void;
  onTrimEnd: (segmentId: string, newEndMs: number) => void;
};

type DragState = {
  segmentId: string;
  handle: TimelineDragHandle;
  currentTimeMs: number;
  minTimeMs: number;
  maxTimeMs: number;
} | null;

type StartDragParams = {
  segmentId: string;
  handle: TimelineDragHandle;
  initialClientX: number;
  initialTimeMs: number;
  minTimeMs: number;
  maxTimeMs: number;
};

export const useTimelineDrag = (
  zoom: number,
  callbacks: TimelineDragCallbacks,
  pixelsPerSecond: number,
  viewportRef?: RefObject<HTMLDivElement | null>,
) => {
  const [isDragging, setIsDragging] = useState(false);
  const [draggingSegmentId, setDraggingSegmentId] = useState<string | null>(
    null,
  );
  const [draggingHandle, setDraggingHandle] =
    useState<TimelineDragHandle | null>(null);
  const [previewTime, setPreviewTime] = useState<number | null>(null);

  const callbacksRef = useRef(callbacks);
  const dragStateRef = useRef<DragState>(null);
  const lastPointerClientXRef = useRef<number | null>(null);
  const activeEdgeRef = useRef<'left' | 'right' | null>(null);
  const edgeEnteredAtRef = useRef<number | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const lastRafTimestampRef = useRef<number | null>(null);

  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  const applyPointerDelta = useCallback(
    (clientX: number): number | null => {
      const dragState = dragStateRef.current;
      if (!dragState) {
        return null;
      }

      const previousClientX = lastPointerClientXRef.current ?? clientX;
      const deltaPixels = clientX - previousClientX;
      const deltaSeconds = pixelToTime(deltaPixels, pixelsPerSecond, zoom);
      const deltaMs = deltaSeconds * 1000;
      lastPointerClientXRef.current = clientX;

      const nextTime = clamp(
        dragState.currentTimeMs + deltaMs,
        dragState.minTimeMs,
        dragState.maxTimeMs,
      );
      dragState.currentTimeMs = nextTime;
      return nextTime;
    },
    [pixelsPerSecond, zoom],
  );

  const clearAutoNudgeAnimation = () => {
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  };

  const stopDragging = useCallback(() => {
    clearAutoNudgeAnimation();
    setIsDragging(false);
    setDraggingSegmentId(null);
    setDraggingHandle(null);
    setPreviewTime(null);
    dragStateRef.current = null;
    lastPointerClientXRef.current = null;
    activeEdgeRef.current = null;
    edgeEnteredAtRef.current = null;
    lastRafTimestampRef.current = null;
  }, []);

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const nextPreviewTime = applyPointerDelta(event.clientX);
      if (nextPreviewTime == null) {
        return;
      }
      setPreviewTime(nextPreviewTime);
    };

    const handleMouseUp = (event: MouseEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) {
        stopDragging();
        return;
      }

      const nextTime = applyPointerDelta(event.clientX);
      if (nextTime != null) {
        if (dragState.handle === 'start') {
          callbacksRef.current.onTrimStart(dragState.segmentId, nextTime);
        } else {
          callbacksRef.current.onTrimEnd(dragState.segmentId, nextTime);
        }
      }

      stopDragging();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, pixelsPerSecond, zoom, applyPointerDelta, stopDragging]);

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const getEdgeNudgeState = (clientX: number) => {
      const viewport = viewportRef?.current;
      if (!viewport) {
        return { edge: null, intensity: 0 };
      }

      const viewportRect = viewport.getBoundingClientRect();
      const leftGutterStart = viewportRect.left - EDGE_GUTTER_PX;
      const rightGutterEnd = viewportRect.right + EDGE_GUTTER_PX;

      if (clientX >= leftGutterStart && clientX <= viewportRect.left) {
        const leftDistanceFromEdge = viewportRect.left - clientX;
        return {
          edge: 'left' as const,
          intensity: clamp(leftDistanceFromEdge / EDGE_GUTTER_PX, 0, 1),
        };
      }

      if (clientX >= viewportRect.right && clientX <= rightGutterEnd) {
        const rightDistanceFromEdge = clientX - viewportRect.right;
        return {
          edge: 'right' as const,
          intensity: clamp(rightDistanceFromEdge / EDGE_GUTTER_PX, 0, 1),
        };
      }

      return { edge: null, intensity: 0 };
    };

    const tick = (timestamp: number) => {
      const dragState = dragStateRef.current;
      const pointerX = lastPointerClientXRef.current;
      if (!dragState || pointerX == null) {
        rafIdRef.current = requestAnimationFrame(tick);
        return;
      }

      const { edge, intensity } = getEdgeNudgeState(pointerX);

      if (edge !== activeEdgeRef.current) {
        activeEdgeRef.current = edge;
        edgeEnteredAtRef.current = edge ? timestamp : null;
      }

      const edgeEnteredAt = edgeEnteredAtRef.current;
      const elapsedAtEdgeMs =
        edge && edgeEnteredAt != null ? timestamp - edgeEnteredAt : 0;
      const frameDeltaMs =
        lastRafTimestampRef.current == null
          ? 0
          : timestamp - lastRafTimestampRef.current;
      lastRafTimestampRef.current = timestamp;

      if (
        edge &&
        elapsedAtEdgeMs >= EDGE_ACTIVATION_MS &&
        frameDeltaMs > 0 &&
        intensity > 0
      ) {
        const speedMsPerSec =
          EDGE_SPEED_MIN_MS_PER_SEC +
          (EDGE_SPEED_MAX_MS_PER_SEC - EDGE_SPEED_MIN_MS_PER_SEC) * intensity;
        const direction = edge === 'left' ? -1 : 1;
        const deltaMs = direction * speedMsPerSec * (frameDeltaMs / 1000);

        const nextTime = clamp(
          dragState.currentTimeMs + deltaMs,
          dragState.minTimeMs,
          dragState.maxTimeMs,
        );
        if (nextTime !== dragState.currentTimeMs) {
          dragState.currentTimeMs = nextTime;
          setPreviewTime(nextTime);
        }
      }

      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);

    return () => {
      clearAutoNudgeAnimation();
      activeEdgeRef.current = null;
      edgeEnteredAtRef.current = null;
      lastRafTimestampRef.current = null;
    };
  }, [isDragging, viewportRef]);

  const startDrag = ({
    segmentId,
    handle,
    initialClientX,
    initialTimeMs,
    minTimeMs,
    maxTimeMs,
  }: StartDragParams) => {
    dragStateRef.current = {
      segmentId,
      handle,
      currentTimeMs: initialTimeMs,
      minTimeMs,
      maxTimeMs,
    };
    lastPointerClientXRef.current = initialClientX;
    activeEdgeRef.current = null;
    edgeEnteredAtRef.current = null;
    lastRafTimestampRef.current = null;
    setIsDragging(true);
    setDraggingSegmentId(segmentId);
    setDraggingHandle(handle);
    setPreviewTime(initialTimeMs);
  };

  return {
    isDragging,
    draggingSegmentId,
    draggingHandle,
    previewTime,
    startDrag,
  };
};
